/**
 * Admin API client. 走 Telegram OAuth Gateway：
 *   - access_token 由后端在 oauth-callback 写入 httpOnly cookie，浏览器自动携带
 *   - fetch 全部 credentials: "include"
 *   - 收 401 -> 跳 /oauth/authorize 重登（无 refresh_token grant）
 */

export interface ApiErrorBody {
  readonly code: string;
  readonly message: string;
}

export interface ParseIssue {
  readonly path: ReadonlyArray<string>;
  readonly message: string;
}

export class AdminApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly issues: ReadonlyArray<ParseIssue>;
  constructor(
    code: string,
    message: string,
    status: number,
    issues: ReadonlyArray<ParseIssue> = [],
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.issues = issues;
  }
}

const ZH_ERR: Record<string, string> = {
  UNAUTHORIZED: "未登录或登录已过期",
  FORBIDDEN: "无权限执行此操作",
  ADMIN_NOT_AUTHENTICATED: "未登录或登录已过期，请重新登录",
  ADMIN_FORBIDDEN: "无权限执行此操作（OAuth 用户组未匹配）",
  OAUTH_CALLBACK_INVALID: "OAuth 回调失败，请重试登录",
  INVALID_REQUEST: "请求参数有误，请检查",
  INVALID_BOT_TOKEN: "Bot token 无效，无法连接 Telegram",
  CHANNEL_NOT_REACHABLE: "机器人尚未加入该频道，请先邀请机器人",
  ALREADY_EXISTS: "该频道已绑定过，请勿重复添加",
  PRIZE_WEIGHT_INVALID: "奖品权重必须为非负整数",
  USER_NOT_FOUND: "用户不存在",
  RATE_LIMITED: "操作过于频繁，请稍后再试",
  INTERNAL_ERROR: "系统错误，请联系管理员",
  NOT_FOUND: "资源不存在",
  MANAGER_BOT_DISABLED: "管理 Bot 已禁用",
  MANAGER_BOT_NOT_FOUND: "管理 Bot 不存在",
  ONBOARD_TOKEN_EXPIRED: "一键创建会话已过期，请重新发起",
  ONBOARD_TOKEN_INVALID: "一键创建会话无效",
  MANAGED_BOT_OWNER_MISMATCH: "Telegram 操作账号与创建会话的账号不一致",
  BOT_NOT_READY: "Bot 配置未完成，无法启用",
  BOT_NOT_REACHABLE: "无法访问 Telegram Bot（token 失效或网络异常）",
};

const GENERIC_BACKEND_MSG = new Set([
  "",
  "internal",
  "internal error",
  "internal_error",
  "internal server error",
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "unknown",
]);

const isGenericBackendMessage = (message: string, code: string): boolean => {
  const t = message.trim().toLowerCase();
  if (GENERIC_BACKEND_MSG.has(t)) return true;
  if (t === code.toLowerCase()) return true;
  if (t === code.toLowerCase().replaceAll("_", " ")) return true;
  return false;
};

/** 错误码中文；有具体后端 message（如唯一约束）时优先展示，避免只剩「系统错误」。 */
export const adminErrText = (code: string, fallback?: string): string => {
  const mapped = ZH_ERR[code];
  const detail = fallback?.trim();
  if (detail !== undefined && detail !== "" && !isGenericBackendMessage(detail, code)) {
    return detail;
  }
  return mapped ?? (detail !== undefined && detail !== "" ? detail : "未知错误");
};

export const adminErrorMessage = (e: unknown): string => {
  if (e instanceof AdminApiError) {
    const fromIssues = e.issues
      .map((i) => i.message.trim())
      .filter((m) => m !== "")
      .join("；");
    const detail = e.message.trim() !== "" ? e.message : fromIssues;
    return adminErrText(e.code, detail === "" ? undefined : detail);
  }
  if (e instanceof Error && e.message.trim() !== "") return e.message;
  return "未知错误";
};

interface ErrorBody {
  readonly error?: ApiErrorBody;
  readonly details?: { issues?: Array<{ path?: string[]; message?: string }> };
}

/** 临时诊断：Console 过滤 `[mip-oauth-debug]` 看谁触发重登。查完可删。 */
const oauthDebug = (msg: string, extra?: Record<string, unknown>): void => {
  if (extra !== undefined) {
    console.warn(`[mip-oauth-debug] ${msg}`, extra);
  } else {
    console.warn(`[mip-oauth-debug] ${msg}`);
  }
};

const handle = async <T>(res: Response, reqLabel: string): Promise<T> => {
  if (res.status === 401 || res.status === 403) {
    oauthDebug("API 鉴权失败响应", {
      status: res.status,
      url: res.url,
      req: reqLabel,
      at: new Date().toISOString(),
    });
  }
  if (!res.ok) {
    let body: ErrorBody = {};
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      const err = new AdminApiError("INTERNAL_ERROR", res.statusText, res.status);
      if (res.status === 401 || res.status === 403) {
        oauthDebug("API 鉴权失败且 body 非 JSON", {
          status: res.status,
          req: reqLabel,
          statusText: res.statusText,
        });
      }
      throw err;
    }
    const rawIssues = body.details?.issues ?? [];
    const issues: ParseIssue[] = rawIssues.map((i) => ({
      path: i.path ?? [],
      message: i.message ?? "",
    }));
    const err = new AdminApiError(
      body.error?.code ?? "INTERNAL_ERROR",
      body.error?.message ?? res.statusText,
      res.status,
      issues,
    );
    (err as unknown as { details?: unknown }).details = body.details;
    if (res.status === 401 || res.status === 403) {
      oauthDebug("API 鉴权失败将 throw AdminApiError", {
        status: res.status,
        code: err.code,
        message: err.message,
        req: reqLabel,
        url: res.url,
      });
    }
    throw err;
  }
  // 204 No Content：无 body，跳过 JSON 解析（否则空 body 触发 SyntaxError）。
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
};

const baseHeaders = (body: boolean): HeadersInit => {
  const h: Record<string, string> = {};
  if (body) h["Content-Type"] = "application/json";
  return h;
};

export const adminGet = async <T>(path: string): Promise<T> => {
  const res = await fetch(`/api/v1/admin${path}`, {
    method: "GET",
    credentials: "include",
    headers: baseHeaders(false),
  });
  return handle<T>(res, `GET ${path}`);
};

export const adminSend = async <T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> => {
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: baseHeaders(true),
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(`/api/v1/admin${path}`, init);
  return handle<T>(res, `${method} ${path}`);
};

// ============================================================
// OAuth helpers
// ============================================================

const STATE_KEY = "mip:admin:oauth:state";
const VERIFIER_KEY = "mip:admin:oauth:code_verifier";
const POST_LOGIN_REDIRECT_KEY = "mip:admin:oauth:post_login";

const env = (key: string): string => {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  if (v === undefined || v === "") {
    throw new Error(`missing env ${key}`);
  }
  return v;
};

const b64UrlEncode = (bytes: Uint8Array): string => {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomBytes = (n: number): Uint8Array => {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return a;
};

const sha256Base64Url = async (input: string): Promise<string> => {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return b64UrlEncode(new Uint8Array(digest));
};

export const beginOAuthLogin = async (
  postLoginPath?: string,
  reason = "unspecified",
): Promise<void> => {
  const stack = new Error("beginOAuthLogin stack").stack ?? "";
  oauthDebug("即将跳转 OAuth authorize（整页）", {
    reason,
    postLoginPath: postLoginPath ?? null,
    currentPath: window.location.pathname + window.location.search,
    at: new Date().toISOString(),
    stack,
  });

  const issuer = env("VITE_MIP_OAUTH_ISSUER");
  const clientId = env("VITE_MIP_OAUTH_CLIENT_ID");
  const redirectUri = env("VITE_MIP_OAUTH_REDIRECT_URI");
  const scope = (import.meta.env as Record<string, string>)["VITE_MIP_OAUTH_SCOPE"] ?? "profile";

  const state = b64UrlEncode(randomBytes(16));
  const verifier = b64UrlEncode(randomBytes(32));
  const challenge = await sha256Base64Url(verifier);

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  if (postLoginPath !== undefined) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, postLoginPath);
  }

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  const authorizeUrl = `${issuer}/oauth/authorize?${params.toString()}`;
  oauthDebug("location.href -> authorize", { authorizeUrl: authorizeUrl.slice(0, 200) + "..." });
  window.location.href = authorizeUrl;
};

/** 判断错误是否为鉴权失效（401 / 403）。 */
export const isAuthError = (err: unknown): boolean => {
  const status = (err as { status?: number }).status;
  return status === 401 || status === 403;
};

/**
 * 鉴权失效时触发重新登录。
 * 先探测 /auth/me：若仍成功说明是单接口误报（如 readiness 抖动），禁止整页重登。
 * 仅 me 也 401/403 才跳 OAuth（无 refresh_token）。
 */
let reloginTriggered = false;
let reloginConfirming = false;
export const triggerReLogin = (source = "unknown", err?: unknown): void => {
  if (reloginTriggered) {
    oauthDebug("triggerReLogin 已触发过，忽略", { source });
    return;
  }
  if (reloginConfirming) {
    oauthDebug("triggerReLogin 正在确认 /auth/me，忽略", { source });
    return;
  }
  const path = window.location.pathname + window.location.search;
  const status = (err as { status?: number } | undefined)?.status;
  const code = (err as { code?: string } | undefined)?.code;
  const message = (err as { message?: string } | undefined)?.message;
  oauthDebug("triggerReLogin 先确认 /auth/me", {
    source,
    path,
    status,
    code,
    message,
    at: new Date().toISOString(),
  });
  reloginConfirming = true;
  void (async () => {
    try {
      // 直接 fetch，避免再走 QueryCache.onError 递归
      const res = await fetch(`/api/v1/admin/auth/me`, {
        method: "GET",
        credentials: "include",
        headers: baseHeaders(false),
      });
      if (res.ok) {
        oauthDebug("triggerReLogin 取消：/auth/me 仍成功（单接口误报）", {
          source,
          failedStatus: status,
          failedCode: code,
        });
        return;
      }
      oauthDebug("triggerReLogin 确认 me 也失败，整页重登", {
        source,
        meStatus: res.status,
      });
    } catch (probeErr) {
      oauthDebug("triggerReLogin me 探测网络异常，仍重登", {
        source,
        error: String(probeErr),
      });
    } finally {
      reloginConfirming = false;
    }
    if (reloginTriggered) return;
    reloginTriggered = true;
    oauthDebug("triggerReLogin -> beginOAuthLogin", {
      source,
      path,
      status,
      code,
      message,
      at: new Date().toISOString(),
      stack: new Error("triggerReLogin stack").stack,
    });
    void beginOAuthLogin(path, `triggerReLogin:${source}`).catch((e) => {
      oauthDebug("beginOAuthLogin 失败", { error: String(e) });
      reloginTriggered = false;
    });
  })();
};

export const completeOAuthLogin = async (
  code: string,
  state: string,
): Promise<{ ok: true; redirectTo: string }> => {
  const storedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (storedState === null || verifier === null) {
    throw new AdminApiError(
      "OAUTH_CALLBACK_INVALID",
      "OAuth state/code_verifier 缺失，请重新登录",
      400,
    );
  }
  if (storedState !== state) {
    throw new AdminApiError("OAUTH_CALLBACK_INVALID", "state 不匹配，可能被劫持", 400);
  }
  await adminSend<{ ok: true; expiresIn: number }>("POST", "/auth/oauth-callback", {
    code,
    codeVerifier: verifier,
    state,
  });
  const redirectTo = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ?? "/";
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return { ok: true, redirectTo };
};

export const oauthLogout = async (): Promise<string> => {
  const res = await adminSend<{ ok: true; redirectUrl: string }>("POST", "/auth/logout");
  return res.redirectUrl;
};

/** 上传图片 / 音频到 /uploads/asset，返回可公开访问的 url。 */
export const adminUpload = async (file: File): Promise<{ url: string }> => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/v1/admin/uploads/asset`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  return handle<{ url: string }>(res, "POST /uploads/asset");
};

/** 上传 bot 的 Telegram 头像（multipart）。后端调 setMyProfilePhoto。 */
export const adminUploadBotAvatar = async (botId: string, file: File): Promise<{ ok: true }> => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/v1/admin/bots/${botId}/avatar`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  return handle<{ ok: true }>(res, `POST /bots/${botId}/avatar`);
};

// ============================================================
// Analytics types
// ============================================================

export type AnalyticsPlatform = "facebook" | "tiktok" | "google" | "sharechat";
export type AnalyticsClickIdType = "fbclid" | "ttclid" | "gclid" | "wbraid" | "gbraid";
export type AnalyticsConversionStatus = "pending" | "sent" | "failed" | "skipped";
/** Google Data Manager requestStatus 第二阶段（仅 platform=google） */
export type AnalyticsGoogleIngestStatus =
  | "processing"
  | "success"
  | "failed"
  | "partial_success"
  | "stale";
export type AnalyticsPoolStatus = "available" | "assigned";

export type AnalyticsEventReportItem = {
  readonly enabled: boolean;
  readonly eventName: string | null;
};

export interface AnalyticsConfig {
  readonly events: {
    readonly visit: AnalyticsEventReportItem;
    readonly click: AnalyticsEventReportItem;
    readonly join: AnalyticsEventReportItem;
    readonly joinUnique: AnalyticsEventReportItem;
    readonly leave: AnalyticsEventReportItem;
  };
  readonly dedupEnabled: boolean;
  readonly reportOnLeave: boolean;
  readonly defaultEventName: string | null;
  /** 落地页背景（bot 级默认），/uploads/ URL */
  readonly landingCardBgUrl?: string | null;
  readonly landingPageBgUrl?: string | null;
}

export type AnalyticsAttributionHealth = "ok" | "degraded_public" | "bot_not_admin" | "unknown";

export type AnalyticsAttributionMode = "auto" | "force_ok" | "force_degraded";

export interface AnalyticsTargetChat {
  readonly id: string;
  readonly chatId: number;
  readonly chatType: "channel" | "supergroup";
  readonly chatToken: string;
  readonly title: string | null;
  readonly isEnabled: boolean;
  readonly fallbackInviteLink: string | null;
  readonly poolMinThreshold: number | null;
  readonly reclaimTimeoutMin: number | null;
  readonly createdAt: string;
  /** 群备注（运营名）；可空 */
  readonly remark: string | null;
  /** 运营标签（多选）；空数组 = 未打标 */
  readonly tags: readonly string[];
  /** 落地页背景（频道覆盖，null 继承 bot 默认），/uploads/ URL */
  readonly landingCardBgUrl: string | null;
  readonly landingPageBgUrl: string | null;
  /** 落地页预览 URL（带 HMAC 签名，预览不落 click / hit） */
  readonly previewUrl: string;
  /** TG 现名（cron/sync 刷新；同步时 title 亦覆盖为该值） */
  readonly metaTgTitle: string | null;
  /** 公开频道 username，无 @；私有 null */
  readonly metaUsername: string | null;
  readonly isPublic: boolean;
  readonly attributionMode: AnalyticsAttributionMode;
  readonly attributionHealth: AnalyticsAttributionHealth;
  /** 中文告警文案；可空 */
  readonly attributionWarning: string | null;
}

/** GET /analytics/:botId/chats 分页列表（默认 pageSize=20） */
export interface AnalyticsTargetChatsPage {
  readonly items: ReadonlyArray<AnalyticsTargetChat>;
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly totalPages: number;
  readonly summary: {
    readonly total: number;
    readonly untagged: number;
    readonly degradedCount: number;
    readonly tags: ReadonlyArray<{ readonly name: string; readonly count: number }>;
  };
}

/** 频道列表默认每页条数（全量投放 badge，页宜小以防卡顿） */
export const TARGET_CHAT_LIST_PAGE_SIZE = 10;

export interface AnalyticsCredential {
  readonly id: string;
  readonly platform: AnalyticsPlatform;
  readonly externalId: string;
  readonly accessTokenMasked: string;
  readonly isEnabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly appId: string | null;
  readonly authStatus: "pending" | "authorized" | "expired" | null;
  readonly accessTokenExpiresAt: string | null;
  readonly hasSecret: boolean;
  // Google 字段（facebook / tiktok 行为 null）
  readonly googleSaEmail: string | null;
  readonly googleOperatingCustomerId: string | null;
  readonly googleLoginCustomerId: string | null;
  readonly hasGoogleSaKey: boolean;
  /** 运营备注（投放名称等）；可空 */
  readonly remark: string | null;
}

export interface AnalyticsAd {
  readonly id: string;
  readonly platform: AnalyticsPlatform;
  readonly adId: string;
  readonly eventName: string | null;
  readonly firstSeenAt: string;
  readonly conversionCount: number;
  // Google ad 配置；FB/TikTok 行为 null
  readonly googleConversionActionId: string | null;
  // ad 维度 pin 到 google 凭据（多 google 凭据时必填，否则上报跳过）；FB/TikTok 行为 null
  readonly credentialId: string | null;
  /** 广告备注（素材/计划运营名）；可空 */
  readonly remark: string | null;
  /** 软删时间；null 表示有效 */
  readonly deletedAt: string | null;
}

export interface AnalyticsConversion {
  readonly id: string;
  readonly platform: AnalyticsPlatform;
  readonly adId: string;
  readonly attributionTier: AnalyticsConversionAttributionTier;
  /** 实际上报使用的 Pixel / Dataset ID（来自 click 绑定的凭据；FB 失败记录可从上报 URL 兜底解析） */
  readonly pixelId: string | null;
  readonly tgUserId: number | null;
  readonly eventType: "visit" | "click" | "join" | "join_unique" | "leave";
  readonly occurredAt: string;
  readonly reportStatus: AnalyticsConversionStatus;
  readonly reportAttempts: number;
  readonly lastError: string | null;
  /** 实际上报到 FB/TikTok 的事件名（如 CompleteRegistration）；null 表示尚未上报或已跳过 */
  readonly reportedEventName: string | null;
  /** Admin 手动重试时间；非 null 表示曾点过重试 */
  readonly manualRetriedAt: string | null;
  /** Google events:ingest 返回的 requestId；非 google 为 null */
  readonly googleRequestId: string | null;
  /** Google requestStatus 入库状态；非 google / 未初始化为 null */
  readonly googleIngestStatus: AnalyticsGoogleIngestStatus | null;
  readonly googleIngestCheckedAt: string | null;
  readonly googleIngestDetail: string | null;
  /** 最近一次成功上报时用的 Google ctId */
  readonly reportedConversionActionId: string | null;
  /** 因换 ctId 等被重入队的时间 */
  readonly reportSupersededAt: string | null;
}

export interface AnalyticsPoolChatStat {
  readonly chatId: number;
  readonly title: string | null;
  readonly isEnabled: boolean;
  readonly available: number;
  readonly assigned: number;
}

export type AnalyticsClickStatus = "pending" | "joined" | "abandoned";

export type AnalyticsAttributionTier = "per_click" | "per_ad" | "natural";

export type AnalyticsConversionAttributionTier = "per_click" | "per_ad";

export type AnalyticsShortlinkDisposition =
  | "attributed"
  | "cookie_reuse"
  | "non_human"
  | "rate_limited"
  | "shared_fallback"
  | "macro_invalid"
  | "per_ad_primary"
  | "per_ad_fallback"
  | "natural_fallback";

/** 频道 Google 投放链接（per-ad 主路径：一个广告一条共享邀请链） */
export interface AnalyticsChatGoogleLink {
  readonly id: string;
  readonly adId: string;
  readonly remark: string | null;
  readonly googleConversionActionId: string | null;
  readonly credentialId: string | null;
  readonly customerId: string | null;
  readonly inviteLink: string | null;
}

export interface AnalyticsShortlinkVisit {
  readonly id: string;
  readonly disposition: AnalyticsShortlinkDisposition;
  readonly platform: string | null;
  readonly adId: string;
  readonly rawClickId: string | null;
  readonly clickRef: string | null;
  readonly ip: string | null;
  readonly country: string | null;
  readonly ua: string | null;
  readonly landingUrl: string | null;
  readonly referer: string | null;
  readonly clickStatus: AnalyticsClickStatus | null;
  readonly attributionTier: AnalyticsAttributionTier | null;
  readonly tgUserId: number | null;
  readonly createdAt: string;
  readonly joinedAt: string | null;
}

export type AnalyticsUntrackedJoinSource =
  | "no_invite_link"
  | "fallback_link"
  | "unmatched_link"
  | "direct_join";

export interface AnalyticsUntrackedJoin {
  readonly id: string;
  readonly chatId: number;
  readonly chatTitle: string | null;
  readonly tgUserId: number;
  readonly joinSource: AnalyticsUntrackedJoinSource;
  readonly inviteLink: string | null;
  readonly inviteLinkKey: string | null;
  readonly occurredAt: string;
}

export interface AnalyticsClick {
  readonly id: string;
  readonly platform: string | null;
  readonly adId: string;
  readonly attributionTier: AnalyticsAttributionTier;
  readonly clickId: string | null;
  readonly clickIdType: AnalyticsClickIdType | null;
  readonly ip: string | null;
  readonly country: string | null;
  readonly ua: string | null;
  readonly landingUrl: string | null;
  readonly referer: string | null;
  readonly status: AnalyticsClickStatus;
  readonly tgUserId: number | null;
  readonly createdAt: string;
  readonly joinedAt: string | null;
}

/**
 * Admin 分页列表响应。
 * 大表已改为 keyset：total / totalPages 为 null；用 hasMore + nextCursor 翻页。
 * pageSize 硬锁 30。
 */
export interface PaginatedResponse<T> {
  readonly items: ReadonlyArray<T>;
  readonly total: number | null;
  readonly page: number;
  readonly pageSize: number;
  readonly totalPages: number | null;
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
}

/** Admin 大表列表硬锁，禁止放大。 */
export const ADMIN_LIST_PAGE_SIZE = 30;
